import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import type { CompanyPipelineDto } from '@lib/dto/pipeline.dto'
import type { FunnelTarget } from '@lib/enums/funnel-stage.enum'
import { venueStandout } from '@lib/helpers/pipeline-story'
import { venueBookedChange, venuesLookFirst } from '@lib/helpers/pipeline-text'
import { Table, TableBody, TableCell, TableEmptyRow, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { wholePercent } from '@/components/stats/StatsPieces'
import { formatCount, formatMicros } from '@/lib/adFormat'
import { joinNames } from '@/lib/joinNames'
import { cn } from '@/lib/utils'

type Row = CompanyPipelineDto['rows'][number]
type Value = Row['values'][number]

/** A cost this much above the company's own reads as one to look at. */
const COSTLY = 1.25
/** A change in inquiries this big, and this many couples, reads as one to look at. */
const BIG_DROP = 0.25
const BIG_DROP_COUPLES = 10
/** Below this many couples, a change is said in couples, not as a share. */
const SMALL_BASE = 10

const number = 'text-right font-mono tabular-nums'
const attention = 'bg-hold-soft font-semibold text-hold-ink'

/**
 * A cell that needs a look: shaded, with a mark and, for whoever can't see the
 * shade, the reason in words. Never color alone.
 */
function Flagged({ reason, children }: { reason: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center justify-end gap-1">
      <AlertTriangle aria-hidden className="h-3 w-3 shrink-0" />
      {children}
      <span className="sr-only">, {reason}</span>
    </span>
  )
}

/** A change with its sign, a true minus for a fall: "+6%", "−9", "0". */
function signed(change: number, format: (n: number) => string): string {
  if (change === 0) return format(0)
  return `${change > 0 ? '+' : '−'}${format(Math.abs(change))}`
}

function valueOf(values: readonly Value[], target: FunnelTarget): Value | undefined {
  return values.find((v) => v.target === target)
}

/** A venue nobody inquired at or booked, now or in the days compared with: a line, not a row of zeros. */
function quiet(row: Row): boolean {
  const inquiry = valueOf(row.values, 'inquiry')
  const booked = valueOf(row.values, 'booked')
  return (inquiry?.value ?? 0) === 0 && (inquiry?.previous ?? 0) === 0 && (booked?.value ?? 0) === 0 && (booked?.previous ?? 0) === 0
}

/** A share that rounds to nothing reads as no change, not "+0%". */
const ROUNDS_TO_NOTHING = 0.005

function InquiryChange({ value }: { value: Value | undefined }) {
  if (value?.value == null || value.previous === null) return <TableCell className={cn(number, 'text-ink-muted')}>—</TableCell>
  const diff = value.value - value.previous
  // A share of a handful (or of none) says nothing: below ten couples, the change in couples.
  if (value.previous < SMALL_BASE) {
    return <TableCell className={cn(number, diff > 0 ? 'text-ok-ink' : 'text-ink-muted')}>{signed(diff, formatCount)}</TableCell>
  }
  const change = diff / value.previous
  if (Math.abs(change) < ROUNDS_TO_NOTHING) return <TableCell className={cn(number, 'text-ink-muted')}>0%</TableCell>
  const said = signed(change, wholePercent)
  if (change <= -BIG_DROP && -diff >= BIG_DROP_COUPLES) {
    return (
      <TableCell className={cn(number, attention)}>
        <Flagged reason="a big drop">{said}</Flagged>
      </TableCell>
    )
  }
  return <TableCell className={cn(number, change > 0 ? 'text-ok-ink' : 'text-ink-muted')}>{said}</TableCell>
}

/** Toured out of inquiries; "Not tracked" where nothing records a tour. */
function TourRate({ row }: { row: Row }) {
  const toured = valueOf(row.values, 'toured')
  if (toured?.value == null) return <TableCell className="text-right text-small text-ink-muted">Not tracked</TableCell>
  return <TableCell className={number}>{toured.shareOfInquiries === null ? '—' : wholePercent(toured.shareOfInquiries)}</TableCell>
}

/** Booked out of the couples who toured, shaded where it's below what's usual at the venue. */
function TourToBooked({ row }: { row: Row }) {
  const booked = valueOf(row.values, 'booked')
  if (booked === undefined || !booked.before.includes('toured')) return <TableCell className="text-right text-small text-ink-muted">Not tracked</TableCell>
  const usual = booked.usual
  const share = usual === null ? booked.shareOfPrevious : usual.part / usual.whole
  const text = share === null ? '—' : wholePercent(share)
  const below = usual?.verdict === 'below'
  return (
    <TableCell className={cn(number, below && attention, usual?.verdict === 'above' && 'text-ok-ink')}>
      {below ? <Flagged reason="below usual">{text}</Flagged> : text}
      {/* Said, not hidden in a hover: only the couples old enough to judge are counted. */}
      {usual?.settledOnly === true ? (
        <span className="block font-sans text-xs font-normal text-ink-muted">of {formatCount(usual.whole)} judged</span>
      ) : null}
    </TableCell>
  )
}

function BookedChange({ row }: { row: Row }) {
  const diff = venueBookedChange(row)
  if (diff === null) return <TableCell className={cn(number, 'text-ink-muted')}>—</TableCell>
  return (
    <TableCell className={cn(number, 'font-semibold', diff > 0 && 'text-ok-ink', diff < 0 && 'text-danger-ink', diff === 0 && 'font-normal text-ink-muted')}>
      {signed(diff, formatCount)}
    </TableCell>
  )
}

function CostPerBooking({ row, company }: { row: Row; company: number | null }) {
  const booked = valueOf(row.values, 'booked')
  const currency = row.cost?.currencyCode ?? null
  if (booked?.costPerMicros == null || currency === null) return <TableCell className={cn(number, 'text-ink-muted')}>—</TableCell>
  const said = formatMicros(booked.costPerMicros, currency)
  if (company === null || booked.costPerMicros < company * COSTLY) return <TableCell className={number}>{said}</TableCell>
  return (
    <TableCell className={cn(number, attention)}>
      <Flagged reason="well above the company’s">{said}</Flagged>
    </TableCell>
  )
}

interface CompanyVenuesProps {
  data: CompanyPipelineDto
  /** Where a venue's name goes: its own Pipeline, in the same weeks. */
  linkTo: (venueId: string) => string
}

/**
 * The company's venues, ranked by what needs a look (docs/pipeline-view.spec.md,
 * "The company page"): bookings furthest behind the days compared with first,
 * each venue's rates beside the company's, a cell shaded where it's below the
 * venue's usual or well above the company's cost, and what stands out in words.
 * Venues with nobody in the weeks shown are named in a line, not rows of zeros.
 */
export function CompanyVenues({ data, linkTo }: CompanyVenuesProps) {
  const active = venuesLookFirst(data.rows.filter((r) => !quiet(r)))
  const silent = data.rows.filter(quiet)
  const money = data.cost !== null || data.rows.some((r) => r.cost !== null)
  const cohort = data.view === 'cohort'
  const total = (target: FunnelTarget) => data.stages.find((s) => s.target === target)
  const companyBookingCost = total('booked')?.costPerMicros ?? null
  const columns = 6 + (cohort ? 2 : 0) + (money ? 2 : 0)
  const against = data.compare === 'last_year' ? 'vs last year' : 'vs weeks before'
  return (
    <section id="venues" aria-label="Venues" className="space-y-2">
      <Table heading="Venues, the ones that need a look first">
        <TableHeader>
          <TableHead>Venue</TableHead>
          <TableHead className="text-right">Inquiries</TableHead>
          <TableHead className="text-right">
            <span className="sr-only">Inquiries </span>
            {against}
          </TableHead>
          {cohort ? <TableHead className="text-right">Inquiry to tour</TableHead> : null}
          {cohort ? <TableHead className="text-right">Tour to booked</TableHead> : null}
          <TableHead className="text-right">Booked</TableHead>
          <TableHead className="text-right">
            <span className="sr-only">Booked </span>
            {against}
          </TableHead>
          {money ? <TableHead className="text-right">Cost per booking</TableHead> : null}
          {money ? <TableHead className="text-right">Return per $1</TableHead> : null}
          <TableHead>What stands out</TableHead>
        </TableHeader>
        <TableBody>
          {active.length === 0 ? <TableEmptyRow colSpan={columns}>No couples at any venue in these weeks.</TableEmptyRow> : null}
          {active.map((row) => {
            const standout = venueStandout(row, data.compare)
            return (
              <TableRow key={row.venueId}>
                <TableCell className="min-w-40">
                  <Link to={linkTo(row.venueId)} className="font-medium text-ink underline-offset-2 hover:underline">
                    {row.venueName}
                  </Link>
                </TableCell>
                <TableCell className={number}>{formatCount(valueOf(row.values, 'inquiry')?.value ?? 0)}</TableCell>
                <InquiryChange value={valueOf(row.values, 'inquiry')} />
                {cohort ? <TourRate row={row} /> : null}
                {cohort ? <TourToBooked row={row} /> : null}
                <TableCell className={cn(number, 'font-semibold')}>{formatCount(valueOf(row.values, 'booked')?.value ?? 0)}</TableCell>
                <BookedChange row={row} />
                {money ? <CostPerBooking row={row} company={companyBookingCost} /> : null}
                {money ? (
                  <TableCell className={number}>{row.sourceSummary?.returnRatio == null ? '—' : `$${row.sourceSummary.returnRatio.toFixed(2)}`}</TableCell>
                ) : null}
                <TableCell className={cn('min-w-48 whitespace-normal text-control', standout === null && 'text-ink-muted')}>{standout ?? 'Steady'}</TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      {silent.length === 0 ? null : (
        <p className="m-0 text-small text-ink-muted">
          No couples in these weeks at {joinNames(silent.map((r) => r.venueName))}.
        </p>
      )}
    </section>
  )
}
